import dayjs from 'dayjs';
import { CampaignConfigSchema, MessageTemplate } from '../../types';
import { CampaignFormValues, FormMessage } from '../../types';
import { MessageType, ScheduledType } from '../../types';
import { getDefaultOptOutText, isOptOutEnabledByDefault } from '../../helpers';
import { newClientKey } from '../../helpers/clientKey';

/**
 * Default campaign name:
 *   `${dayjs().format('YYYY-MM-DD')} - ${config.name || 'Campaign'}`
 */
export function buildDefaultCampaignName(config: CampaignConfigSchema): string {
  const today = dayjs().format('YYYY-MM-DD');
  return `${today} - ${config.name || 'Campaign'}`;
}

interface DefaultMessageScheduleOpts {
  scheduledType: ScheduledType;
  delay?: string | null;
  /** Type for the fallback message. Unused when the config has templates — they pin their own. */
  fallbackType?: MessageType;
}

/**
 * One form message from one config template. Type resolution: mms-only → 'mms',
 * sms-only → 'sms', else the template's currentMessageType, else 'sms'.
 */
function messageFromTemplate(
  template: MessageTemplate,
  index: number,
  schedule: DefaultMessageScheduleOpts,
): FormMessage {
  const type: MessageType =
    template.messageType === 'mms-only'
      ? 'mms'
      : template.messageType === 'sms-only'
        ? 'sms'
        : (template.currentMessageType || 'sms');

  return {
    clientKey: newClientKey(),
    type,
    text: type === 'mms' ? (template.mmsText || '') : (template.smsText || ''),
    subject: template.mmsSubject || '',
    links: [],
    position: index + 1,
    scheduledType: schedule.scheduledType,
    delay: schedule.delay,
    isSurvey: !!(template.surveyVisible && template.survey?.active),
  };
}

/**
 * One message per config template, or a single fallback message when the config
 * has none. Scheduling comes from the caller: each campaign type anchors
 * differently (annual uses delay '0m', the rest are immediate).
 */
export function buildDefaultMessages(
  config: CampaignConfigSchema,
  schedule: DefaultMessageScheduleOpts,
): FormMessage[] {
  // NOT derived from config.message.types: a template-less config opens as SMS
  // whatever order its allowed types are listed in. Only the flyer asks otherwise.
  const fallbackType = schedule.fallbackType ?? 'sms';
  const templates = config.messageTemplates ?? [];

  if (templates.length > 0) {
    return templates.map((template, index) => messageFromTemplate(template, index, schedule));
  }

  return [
    {
      clientKey: newClientKey(),
      type: fallbackType,
      text: typeof config.message?.text?.value === 'string' ? config.message.text.value : '',
      subject: '',
      links: [],
      position: 1,
      scheduledType: schedule.scheduledType,
      delay: schedule.delay,
    },
  ];
}

/**
 * Enrollment default messages. Same per-template mapping as every other type,
 * plus the three enrollment-only rules:
 *
 *  1. Join templates come first, follow-ups after, so the runtime's
 *     position-based sectioning matches the config's intent.
 *  2. The first follow-up is forced to scheduledType 'time'; the rest take the
 *     template's own schedule when it defines one.
 *  3. Position 1 is always immediate, with no delay or time.
 */
export function buildEnrollmentDefaultMessages(
  config: CampaignConfigSchema,
  schedule: DefaultMessageScheduleOpts,
): FormMessage[] {
  const templates = config.messageTemplates ?? [];
  if (templates.length === 0) {
    return buildDefaultMessages(config, schedule).map(message => ({
      ...message,
      enrollmentSection: 'join' as const,
    }));
  }

  const ordered = [
    ...templates.filter(t => t.messageSection !== 'followup'),
    ...templates.filter(t => t.messageSection === 'followup'),
  ];
  const firstFollowUpIndex = ordered.findIndex(t => t.messageSection === 'followup');

  return ordered
    .map((template, index) => {
      const base = messageFromTemplate(template, index, schedule);
      const templateSchedule = template.schedule;

      return {
        ...base,
        enrollmentSection:
          firstFollowUpIndex !== -1 && index >= firstFollowUpIndex
            ? 'scheduled' as const
            : 'join' as const,
        ...(index === firstFollowUpIndex
          ? { scheduledType: 'time' as ScheduledType }
          : templateSchedule?.scheduledType
            ? { scheduledType: templateSchedule.scheduledType as ScheduledType }
            : {}),
        ...(templateSchedule?.delay ? { delay: templateSchedule.delay } : {}),
        ...(templateSchedule?.time ? { time: templateSchedule.time } : {}),
      };
    })
    .map(message =>
      message.position === 1
        ? { ...message, scheduledType: 'immediate' as ScheduledType, delay: null, time: null }
        : message,
    );
}

/** The fields every policy's getDefaults() sets the same way. */
export function commonDefaults(
  config: CampaignConfigSchema,
): Omit<CampaignFormValues, 'scheduledMessages'> {
  return {
    name: buildDefaultCampaignName(config),
    // Both start empty. `recipients.values` / `keywords.values` are deliberately
    // not read: seeding from them changes the visible form AND the submitted
    // payload for every config carrying stored ids. The only preselection is a
    // group handed in through navigation state, applied in useCampaignForm.
    groupIds: [],
    keywordIds: [],
    dateScheduled: null,
    optOutText: getDefaultOptOutText(config),
    isOptOutEnabled: isOptOutEnabledByDefault(config),
  };
}

/** commonDefaults plus one message per config template. */
export function buildDefaults(
  config: CampaignConfigSchema,
  schedule: DefaultMessageScheduleOpts,
): CampaignFormValues {
  return {
    ...commonDefaults(config),
    scheduledMessages: buildDefaultMessages(config, schedule),
  };
}
